import { useEffect, useState, useCallback, useRef } from 'react';
import {
  adminApi,
  opsApi,
  getErrorMessage,
  type AdminUser,
} from '../lib/api';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'active' | 'shadow' | 'banned' | 'retired';

/** Format a timestamp as a relative "time ago" string. */
function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/** Status badge color classes. */
function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-900 text-green-300';
    case 'shadow': return 'bg-amber-900 text-amber-300';
    case 'banned': return 'bg-red-900 text-red-300';
    case 'retired': return 'bg-gray-700 text-gray-400';
    default: return 'bg-gray-800 text-gray-400';
  }
}

export default function Users() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listUsers({
        status: statusFilter === 'all' ? undefined : statusFilter,
        q: searchQuery || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      if (seq !== loadSeqRef.current) return; // stale response — discard
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      if (seq === loadSeqRef.current) setError(getErrorMessage(err));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [statusFilter, searchQuery, offset]);

  useEffect(() => { load(); }, [load]);

  // Debounce search input → searchQuery (300ms).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setOffset(0);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchInput]);

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setOffset(0);
  };

  const handleBanUnban = async (user: AdminUser, action: 'banned' | 'active') => {
    const actionLabel = action === 'banned' ? 'ban' : 'unban';
    const confirmMsg = action === 'banned'
      ? `Banlamak istediğinize emin misiniz? Tüm yazma işlemleri engellenir. (Değişiklik 15 dk içinde etkili olur)`
      : `Ban'ı kaldırmak istediğinize emin misiniz? (Değişiklik 15 dk içinde etkili olur)`;
    if (!window.confirm(confirmMsg)) return;

    setActionError('');
    setActionSuccess('');
    setActionLoading(user.uid);
    try {
      const result = await opsApi.setUserStatus(user.uid, action);
      setActionSuccess(
        `Kullanıcı ${user.nickname ?? user.uid.slice(0, 8)} ${actionLabel} işlemi: ${result.applied ? 'başarılı' : 'zaten bu durumda'}. Değişiklik 15 dakika içinde tüm cihazlarda etkili olur.`,
      );
      // Reload the list to reflect the new status.
      await load();
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setActionLoading(null);
    }
  };

  const copyUid = async (uid: string) => {
    try {
      await navigator.clipboard.writeText(uid);
      setActionSuccess(`UID kopyalandı: ${uid}`);
    } catch {
      // Clipboard may not be available (e.g. non-secure context).
    }
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Users</h2>

      {/* Cache delay notice */}
      <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-3">
        <p className="text-sm text-blue-200">
          Ban/unban işlemleri 15 dakika içinde tüm cihazlarda etkili olur (status cache TTL).
        </p>
      </div>

      {/* Action feedback */}
      {actionError && (
        <div className="bg-red-900/20 border border-red-800/50 text-red-200 rounded p-3 text-sm">
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="bg-green-900/20 border border-green-800/50 text-green-200 rounded p-3 text-sm">
          {actionSuccess}
        </div>
      )}

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search nickname, display name, or UID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 min-w-[200px] bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-brand-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value as StatusFilter)}
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-brand-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="shadow">Shadow</option>
          <option value="banned">Banned</option>
          <option value="retired">Retired</option>
        </select>
      </div>

      {/* Loading / error */}
      {loading && <div className="text-gray-400">Loading users...</div>}
      {error && <div className="text-red-400">Error: {error}</div>}

      {/* Users table */}
      {!loading && !error && (
        <>
          {users.length === 0 ? (
            <p className="text-gray-500">No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-left">
                    <th className="py-2 px-3">Nickname / Display</th>
                    <th className="py-2 px-3">UID</th>
                    <th className="py-2 px-3">Provider</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Shadow Reason</th>
                    <th className="py-2 px-3">Shadow Expires</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3">Last Seen</th>
                    <th className="py-2 px-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.uid} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-200">
                          {user.nickname ?? <span className="text-gray-500">—</span>}
                        </div>
                        {user.display_name && (
                          <div className="text-xs text-gray-500">{user.display_name}</div>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => copyUid(user.uid)}
                          className="font-mono text-xs text-gray-400 hover:text-gray-200 transition"
                          title="Click to copy full UID"
                        >
                          {user.uid.slice(0, 8)}…
                        </button>
                      </td>
                      <td className="py-2 px-3 text-gray-400">{user.auth_provider}</td>
                      <td className="py-2 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusBadgeClass(user.status)}`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {user.shadow_reason ?? <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {user.shadow_expires_at_ms != null
                          ? new Date(user.shadow_expires_at_ms).toLocaleString()
                          : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {new Date(user.created_at_ms).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {timeAgo(user.last_seen_ms)}
                      </td>
                      <td className="py-2 px-3">
                        {user.status === 'banned' ? (
                          <button
                            onClick={() => handleBanUnban(user, 'active')}
                            disabled={actionLoading === user.uid}
                            className="text-xs px-3 py-1 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white rounded font-medium"
                          >
                            {actionLoading === user.uid ? '...' : 'Unban'}
                          </button>
                        ) : user.status === 'retired' ? (
                          <button
                            disabled
                            className="text-xs px-3 py-1 bg-gray-700 text-gray-500 rounded font-medium cursor-not-allowed"
                            title="Retired users cannot be banned"
                          >
                            Ban
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBanUnban(user, 'banned')}
                            disabled={actionLoading === user.uid}
                            className="text-xs px-3 py-1 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded font-medium"
                          >
                            {actionLoading === user.uid ? '...' : 'Ban'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-sm text-gray-400">
                Page {currentPage} of {totalPages} ({total} users)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 rounded text-sm"
                >
                  Prev
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                  className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 rounded text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
